"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { PreviousSetData } from "@/actions/weight-training";
import { getPreviousWorkoutSets } from "@/actions/weight-training";
import { finalizeWorkoutSnapshot } from "@/actions/weight-training/workout-completion";
import { useAppForeground } from "@/hooks/use-app-foreground";
import { useAuthState } from "@/hooks/use-auth-state";
import {
  useWorkoutLiveActivity,
  WORKOUT_LIVE_ACTIVITY_MAX_AGE_MS,
} from "@/hooks/use-workout-live-activity";
import {
  currentActivityCommandId,
  currentActivityCommandTime,
} from "@/lib/native/activity-command-context";
import { createRandomId } from "@/lib/random-id";
import {
  displayWeight,
  knownWeightKg,
  type WeightUnit,
  weightFromKg,
  weightToKg,
} from "@/lib/weight-training/measurement-contract";
import {
  acknowledgeWorkoutJournal,
  freezeWorkoutSession,
  readWorkoutJournal,
  workoutJournalStorageKey,
  writeWorkoutJournal,
} from "@/lib/weight-training/session-journal";
import { wtKeys } from "@/lib/weight-training-keys";

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

export type SetData = {
  id: string;
  setNumber: number;
  weight: number | null;
  reps: number | null;
  duration: number | null;
  distance?: number | null;
  completedAt?: string | null;
  restSeconds: number | null;
  isCompleted: boolean;
  isPR?: boolean;
};

export type ExerciseSessionMeta = {
  primaryMuscle?: string | null;
  equipment?: string | null;
  imageUrl?: string | null;
  gifUrl?: string | null;
  supersetGroup?: string | null;
};

export type ExerciseData = {
  id: string;
  exerciseId: string | null;
  customExerciseId: string | null;
  name: string;
  primaryMuscle?: string | null;
  equipment?: string | null;
  imageUrl?: string | null;
  gifUrl?: string | null;
  supersetGroup?: string | null;
  trackMode?: "reps" | "time" | "distance";
  sets: SetData[];
  previousSets?: PreviousSetData[];
  isLoadingPreviousSets?: boolean;
};

export type PRCelebrationInfo = {
  prType: "max_weight" | "max_reps" | "max_volume" | "est_1rm";
  value: number;
  exerciseName: string;
};

// Rest timer state - stored in cache for persistence and sync
export type RestTimerState = {
  active: boolean;
  restEndTime: number | null; // Absolute timestamp when rest ends
  totalSeconds: number; // Original duration for progress
};

export type WorkoutSessionState = {
  workoutId: string | null;
  weightUnit?: WeightUnit | null;
  workoutName: string;
  exercises: ExerciseData[];
  startedAt: string | null;
  endedAt?: string;
  disposition?: "completed" | "discarded";
  accountId?: string;
  appliedCommandIds?: string[];
  restTimer: RestTimerState;
};

const DEFAULT_REST_TIMER: RestTimerState = {
  active: false,
  restEndTime: null,
  totalSeconds: 0,
};

const DEFAULT_SESSION: WorkoutSessionState = {
  workoutId: null,
  workoutName: "Workout",
  exercises: [],
  startedAt: null,
  restTimer: DEFAULT_REST_TIMER,
};

// ════════════════════════════════════════════════════════════════════════════
// HOOK: useWorkoutSession
// ════════════════════════════════════════════════════════════════════════════

type UseWorkoutSessionOptions = {
  weightUnit?: WeightUnit;
  defaultRestSeconds?: number;
  timerAutoStart?: boolean;
};

export const WorkoutSessionContext = createContext<ReturnType<
  typeof useWorkoutSessionController
> | null>(null);

/** All workout surfaces share the app's one controller and native publisher. */
export function useWorkoutSession() {
  const session = useContext(WorkoutSessionContext);
  if (!session) throw new Error("WorkoutSessionProvider is required");
  return session;
}

export function useWorkoutSessionController(
  options: UseWorkoutSessionOptions = {},
) {
  const queryClient = useQueryClient();
  const { identity } = useAuthState();
  const accountId = identity?.id;
  const accountRef = useRef(accountId);
  accountRef.current = accountId;

  // Uber-style unified Live Activity for workout + rest
  const {
    startWorkoutActivity,
    updateWorkoutStats,
    startRestPeriod,
    updateRestPeriod,
    endRestPeriod,
    endWorkoutActivity,
    permissionGuidance,
    clearPermissionGuidance,
  } = useWorkoutLiveActivity();

  // ═══════════════════════════════════════════════════════════════════════════
  // SESSION STATE - Durable account-scoped journal; Query is its UI projection.
  // ═══════════════════════════════════════════════════════════════════════════
  const {
    data: session,
    error: recoveryError,
    isPending: isHydratingSession,
  } = useQuery({
    queryKey: wtKeys.activeWorkout(accountId),
    queryFn: () =>
      accountId ? readWorkoutJournal(window.localStorage, accountId) : null,
    enabled: Boolean(accountId),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!accountId) return;
    const refresh = (event: StorageEvent) => {
      if (event.key === workoutJournalStorageKey(accountId))
        void queryClient.invalidateQueries({
          queryKey: wtKeys.activeWorkout(accountId),
        });
    };
    window.addEventListener("storage", refresh);
    return () => window.removeEventListener("storage", refresh);
  }, [accountId, queryClient]);

  // Helper to get current session with fallback
  const currentSession =
    accountId && (!session?.accountId || session.accountId === accountId)
      ? (session ?? DEFAULT_SESSION)
      : DEFAULT_SESSION;

  // Helper to update session (replaces setSession)
  const updateSession = useCallback(
    (updater: (prev: WorkoutSessionState) => WorkoutSessionState) => {
      queryClient.setQueryData<WorkoutSessionState | null>(
        wtKeys.activeWorkout(accountId),
        (cached) => {
          if (!accountId || accountRef.current !== accountId)
            throw new Error("Sign in before recording a workout");
          const prev =
            readWorkoutJournal(window.localStorage, accountId) ?? cached;
          if (prev?.accountId && prev.accountId !== accountId)
            throw new Error("This workout belongs to another account");
          if (prev?.endedAt) return prev;
          const commandId = currentActivityCommandId();
          if (commandId && prev?.appliedCommandIds?.includes(commandId))
            return prev;
          const next = {
            ...updater(prev ?? DEFAULT_SESSION),
            accountId,
            appliedCommandIds: commandId
              ? [...(prev?.appliedCommandIds ?? []), commandId]
              : prev?.appliedCommandIds,
          };
          // A failed durable write must not acknowledge a successful edit.
          writeWorkoutJournal(window.localStorage, accountId, next);
          return next;
        },
      );
    },
    [queryClient, accountId],
  );

  // Check if we have an active workout
  const hasActiveWorkout = !!currentSession.workoutId;

  // ═══════════════════════════════════════════════════════════════════════════
  // ELAPSED TIME - Calculated from startedAt (survives refresh)
  // ═══════════════════════════════════════════════════════════════════════════
  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    if (!currentSession.startedAt) return 0;
    return Math.floor(
      ((currentSession.endedAt
        ? Date.parse(currentSession.endedAt)
        : Date.now()) -
        new Date(currentSession.startedAt).getTime()) /
        1000,
    );
  });

  useEffect(() => {
    if (!currentSession.startedAt) {
      setElapsedSeconds(0);
      return;
    }

    // Calculate initial elapsed time
    setElapsedSeconds(
      Math.floor(
        ((currentSession.endedAt
          ? Date.parse(currentSession.endedAt)
          : Date.now()) -
          new Date(currentSession.startedAt).getTime()) /
          1000,
      ),
    );

    const timer = setInterval(() => {
      if (currentSession.startedAt) {
        setElapsedSeconds(
          Math.floor(
            ((currentSession.endedAt
              ? Date.parse(currentSession.endedAt)
              : Date.now()) -
              new Date(currentSession.startedAt).getTime()) /
              1000,
          ),
        );
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [currentSession.startedAt, currentSession.endedAt]);

  // The 1s interval above is throttled/suspended while the app is backgrounded,
  // so on resume the displayed workout clock can lag until the next tick.
  // Recompute immediately from the absolute startedAt when we return to the
  // foreground. (The rest timer separately resyncs via use-background-timer.)
  useAppForeground(() => {
    if (!currentSession.startedAt) return;
    setElapsedSeconds(
      Math.floor(
        ((currentSession.endedAt
          ? Date.parse(currentSession.endedAt)
          : Date.now()) -
          new Date(currentSession.startedAt).getTime()) /
          1000,
      ),
    );
  });

  // Start Live Activity when workout is detected (handles both mutation path and cache hydration)
  const prevWorkoutIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      accountId &&
      currentSession.workoutId &&
      !currentSession.endedAt &&
      currentSession.workoutId !== prevWorkoutIdRef.current
    ) {
      // New workout detected - start Live Activity with correct start time
      const startedAtMs = currentSession.startedAt
        ? new Date(currentSession.startedAt).getTime()
        : Date.now();
      if (
        startedAtMs &&
        Date.now() - startedAtMs >= WORKOUT_LIVE_ACTIVITY_MAX_AGE_MS
      ) {
        prevWorkoutIdRef.current = currentSession.workoutId;
        return;
      }

      startWorkoutActivity(
        currentSession.workoutName || "Workout",
        startedAtMs,
        { accountId, workoutId: currentSession.workoutId },
      );
      prevWorkoutIdRef.current = currentSession.workoutId;
    } else if (!currentSession.workoutId && prevWorkoutIdRef.current) {
      // Workout ended - clear ref
      prevWorkoutIdRef.current = null;
    }
  }, [
    accountId,
    currentSession.workoutId,
    currentSession.workoutName,
    currentSession.startedAt,
    currentSession.endedAt,
    startWorkoutActivity,
  ]);

  // Update workout Live Activity stats when exercises/sets change
  useEffect(() => {
    if (!currentSession.workoutId || currentSession.endedAt) return;

    const exerciseCount = currentSession.exercises.length;
    const completedSets = currentSession.exercises.reduce(
      (sum, e) => sum + e.sets.filter((s) => s.isCompleted).length,
      0,
    );
    const totalSetCount = currentSession.exercises.reduce(
      (sum, exercise) => sum + exercise.sets.length,
      0,
    );
    // Volume lifted so far — completed sets only, so the number never counts
    // work that has not happened. Shown as "Volume" on the watch.
    const totalVolumeKg = currentSession.exercises.reduce(
      (sum, exercise) =>
        sum +
        exercise.sets.reduce(
          (setSum, set) =>
            set.isCompleted
              ? setSum +
                (knownWeightKg(set.weight, currentSession.weightUnit) ?? 0) *
                  (set.reps ?? 0)
              : setSum,
          0,
        ),
      0,
    );
    const currentExercise =
      currentSession.exercises.find((exercise) =>
        exercise.sets.some((set) => !set.isCompleted),
      ) ?? currentSession.exercises.at(-1);
    const currentSet = currentExercise?.sets.find((set) => !set.isCompleted);

    updateWorkoutStats({
      workoutName: currentSession.workoutName,
      restTimer: currentSession.restTimer,
      preferredWeightUnit: options.weightUnit ?? "kg",
      exerciseCount,
      completedSetCount: completedSets,
      totalSetCount,
      totalVolumeKg,
      currentExercise: currentExercise?.name ?? null,
      currentSetId: currentSet?.id ?? null,
      currentSetNumber: currentSet?.setNumber ?? null,
      currentSetWeightKg: knownWeightKg(
        currentSet?.weight,
        currentSession.weightUnit,
      ),
      currentSetReps: currentSet?.reps ?? null,
      currentSetDurationSeconds: currentSet?.duration ?? null,
      currentSetDistanceMeters: currentSet?.distance ?? null,
      currentSetTrackMode: currentExercise?.trackMode ?? "reps",
    });
  }, [
    currentSession.workoutId,
    currentSession.endedAt,
    currentSession.exercises,
    currentSession.weightUnit,
    currentSession.workoutName,
    currentSession.restTimer,
    options.weightUnit,
    updateWorkoutStats,
  ]);

  const retryWorkoutNotification = useCallback(() => {
    if (!accountId || !currentSession.workoutId || currentSession.endedAt)
      return;
    const startedAtMs = currentSession.startedAt
      ? new Date(currentSession.startedAt).getTime()
      : undefined;
    void startWorkoutActivity(
      currentSession.workoutName || "Workout",
      startedAtMs ?? Date.now(),
      { accountId, workoutId: currentSession.workoutId },
    );
  }, [
    accountId,
    currentSession.endedAt,
    currentSession.startedAt,
    currentSession.workoutId,
    currentSession.workoutName,
    startWorkoutActivity,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // REST TIMER MANAGEMENT - Synced with TanStack cache + Live Activity
  // ─────────────────────────────────────────────────────────────────────────

  // Start rest timer with given seconds
  const startRestTimer = useCallback(
    (seconds: number) => {
      const restEndTime = Date.now() + seconds * 1000;

      updateSession((prev) => ({
        ...prev,
        restTimer: {
          active: true,
          restEndTime,
          totalSeconds: seconds,
        },
      }));

      // Update Live Activity to resting state
      startRestPeriod(seconds);
    },
    [updateSession, startRestPeriod],
  );

  // Update rest timer (for add/subtract time)
  const updateRestTimer = useCallback(
    (newTotalSeconds: number) => {
      if (!currentSession.restTimer.active) return;

      const newRestEndTime = Date.now() + newTotalSeconds * 1000;

      updateSession((prev) => ({
        ...prev,
        restTimer: {
          ...prev.restTimer,
          restEndTime: newRestEndTime,
          totalSeconds: Math.max(prev.restTimer.totalSeconds, newTotalSeconds),
        },
      }));

      // Update Live Activity with new countdown
      updateRestPeriod(newTotalSeconds);
    },
    [currentSession.restTimer.active, updateSession, updateRestPeriod],
  );

  // Native controls may arrive before React observes the previous command.
  // Apply each delta to the durable deadline, not a rendered remaining value.
  const adjustRestTimer = useCallback(
    (seconds: number) => {
      updateSession((prev) => {
        if (!prev.restTimer.active || !prev.restTimer.restEndTime) return prev;
        const now = Date.now();
        const remaining = Math.max(
          0,
          (prev.restTimer.restEndTime - now) / 1000,
        );
        const nextSeconds = Math.max(5, remaining + seconds);
        return {
          ...prev,
          restTimer: {
            ...prev.restTimer,
            restEndTime: now + nextSeconds * 1000,
            totalSeconds: Math.max(prev.restTimer.totalSeconds, nextSeconds),
          },
        };
      });
    },
    [updateSession],
  );

  // Cancel rest timer (dismiss without completion)
  const cancelRestTimer = useCallback(() => {
    updateSession((prev) => ({
      ...prev,
      restTimer: DEFAULT_REST_TIMER,
    }));

    // Switch Live Activity back to workout immediately
    endRestPeriod(false);
  }, [updateSession, endRestPeriod]);

  // Complete rest timer (reached 0)
  const completeRestTimer = useCallback(() => {
    updateSession((prev) => ({
      ...prev,
      restTimer: DEFAULT_REST_TIMER,
    }));

    // Switch Live Activity back with celebration
    endRestPeriod(true);
  }, [updateSession, endRestPeriod]);

  // ─────────────────────────────────────────────────────────────────────────
  // Start Workout Mutation
  // ─────────────────────────────────────────────────────────────────────────
  const ensureWorkoutStarted = useCallback(async (): Promise<string> => {
    if (!accountId || accountRef.current !== accountId)
      throw new Error("Sign in before recording a workout");
    const stored = readWorkoutJournal(window.localStorage, accountId);
    if (stored?.workoutId) {
      queryClient.setQueryData(wtKeys.activeWorkout(accountId), stored);
      return stored.workoutId;
    }
    const workoutId = createRandomId();
    updateSession((prev) => ({
      ...prev,
      workoutId,
      weightUnit: "kg",
      startedAt: new Date().toISOString(),
    }));
    return workoutId;
  }, [queryClient, accountId, updateSession]);

  const updateSetsForExercise = useCallback(
    (exerciseIdx: number, updater: (sets: SetData[]) => SetData[]) => {
      updateSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((exercise, index) =>
          index === exerciseIdx
            ? { ...exercise, sets: updater(exercise.sets) }
            : exercise,
        ),
      }));
    },
    [updateSession],
  );

  const fetchPreviousSets = useCallback(
    (
      id: string,
      exerciseId: string | null,
      customExerciseId: string | null,
    ) => {
      void getPreviousWorkoutSets(
        exerciseId ?? customExerciseId ?? "",
        customExerciseId !== null,
      )
        .then((previousSets) =>
          updateSession((prev) => ({
            ...prev,
            exercises: prev.exercises.map((ex) =>
              ex.id === id
                ? { ...ex, previousSets, isLoadingPreviousSets: false }
                : ex,
            ),
          })),
        )
        .catch(() => {
          /* The recorded session remains usable offline. */
        });
    },
    [updateSession],
  );

  const insertExerciseAt = useCallback(
    async (
      insertIndex: number,
      exerciseId: string | null,
      customExerciseId: string | null,
      name: string,
      meta: ExerciseSessionMeta = {},
    ) => {
      await ensureWorkoutStarted();
      const id = createRandomId();
      updateSession((prev) => {
        const exercises = [...prev.exercises];
        exercises.splice(
          Math.max(0, Math.min(insertIndex, exercises.length)),
          0,
          {
            id,
            exerciseId,
            customExerciseId,
            name,
            ...meta,
            sets: [
              {
                id: createRandomId(),
                setNumber: 1,
                weight: null,
                reps: null,
                duration: null,
                restSeconds: null,
                isCompleted: false,
              },
            ],
            isLoadingPreviousSets: true,
          },
        );
        return { ...prev, exercises };
      });
      fetchPreviousSets(id, exerciseId, customExerciseId);
    },
    [ensureWorkoutStarted, updateSession, fetchPreviousSets],
  );

  const addExercise = useCallback(
    (
      exerciseId: string | null,
      customExerciseId: string | null,
      name: string,
      meta: ExerciseSessionMeta = {},
    ) =>
      insertExerciseAt(
        Number.MAX_SAFE_INTEGER,
        exerciseId,
        customExerciseId,
        name,
        meta,
      ),
    [insertExerciseAt],
  );

  const replaceExercise = useCallback(
    (
      exerciseIdx: number,
      exerciseId: string | null,
      customExerciseId: string | null,
      name: string,
      meta: ExerciseSessionMeta = {},
    ) => {
      const existing = currentSession.exercises[exerciseIdx];
      if (!existing) return;
      updateSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((ex, index) =>
          index === exerciseIdx
            ? {
                ...ex,
                exerciseId,
                customExerciseId,
                name,
                ...meta,
                previousSets: undefined,
                isLoadingPreviousSets: true,
              }
            : ex,
        ),
      }));
      fetchPreviousSets(existing.id, exerciseId, customExerciseId);
    },
    [currentSession.exercises, updateSession, fetchPreviousSets],
  );

  const reorderExercisesByIds = useCallback(
    (ids: string[]) => {
      updateSession((prev) => {
        if (
          ids.length !== prev.exercises.length ||
          new Set(ids).size !== ids.length ||
          ids.some((id) => !prev.exercises.some((ex) => ex.id === id))
        )
          throw new Error("Invalid exercise order");
        return {
          ...prev,
          exercises: ids.map((id) => {
            const exercise = prev.exercises.find((ex) => ex.id === id);
            if (!exercise) throw new Error("Exercise not found");
            return exercise;
          }),
        };
      });
    },
    [updateSession],
  );

  const setExerciseSuperset = useCallback(
    (index: number, supersetGroup: string | null) => {
      updateSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((ex, i) =>
          i === index ? { ...ex, supersetGroup } : ex,
        ),
      }));
    },
    [updateSession],
  );
  const linkExercisesAsSuperset = useCallback(
    (firstIndex: number, secondIndex: number) => {
      updateSession((prev) => {
        const first = prev.exercises[firstIndex],
          second = prev.exercises[secondIndex];
        if (!first || !second) return prev;
        const supersetGroup =
          first.supersetGroup ?? second.supersetGroup ?? createRandomId();
        return {
          ...prev,
          exercises: prev.exercises.map((ex, i) =>
            i === firstIndex || i === secondIndex
              ? { ...ex, supersetGroup }
              : ex,
          ),
        };
      });
    },
    [updateSession],
  );
  const removeExercise = useCallback(
    (index: number) => {
      updateSession((prev) => ({
        ...prev,
        exercises: prev.exercises.filter((_, i) => i !== index),
      }));
    },
    [updateSession],
  );
  const handleAddSet = useCallback(
    (index: number) => {
      updateSetsForExercise(index, (sets) => [
        ...sets,
        {
          id: createRandomId(),
          setNumber: sets.length + 1,
          weight: null,
          reps: null,
          duration: null,
          restSeconds: options.defaultRestSeconds ?? null,
          isCompleted: false,
        },
      ]);
    },
    [updateSetsForExercise, options.defaultRestSeconds],
  );
  const handleUpdateSet = useCallback(
    (
      index: number,
      setId: string,
      field: "weight" | "reps" | "duration" | "distance" | "restSeconds",
      value: number | null,
    ) => {
      if (field === "weight" && currentSession.weightUnit == null)
        throw new Error(
          "Confirm the saved workout's weight unit before editing weights",
        );
      const stored =
        field === "weight" && value != null
          ? weightFromKg(
              weightToKg(value, options.weightUnit ?? "kg"),
              currentSession.weightUnit ?? "kg",
            )
          : value;
      updateSetsForExercise(index, (sets) =>
        sets.map((set) =>
          set.id === setId ? { ...set, [field]: stored } : set,
        ),
      );
    },
    [updateSetsForExercise, options.weightUnit, currentSession.weightUnit],
  );
  const setCompletion = useCallback(
    (setId: string, exerciseId?: string, completed?: boolean) => {
      updateSession((prev) => {
        const exercise = prev.exercises.find(
          (ex) =>
            (!exerciseId || ex.id === exerciseId) &&
            ex.sets.some((set) => set.id === setId),
        );
        const set = exercise?.sets.find((set) => set.id === setId);
        if (!exercise || !set) return prev;
        const isCompleted = completed ?? !set.isCompleted;
        if (isCompleted === set.isCompleted) return prev;
        const restSeconds = set.restSeconds ?? options.defaultRestSeconds ?? 90;
        return {
          ...prev,
          restTimer:
            isCompleted && options.timerAutoStart
              ? {
                  active: true,
                  restEndTime: Date.now() + restSeconds * 1000,
                  totalSeconds: restSeconds,
                }
              : prev.restTimer,
          exercises: prev.exercises.map((ex) =>
            ex.id !== exercise.id
              ? ex
              : {
                  ...ex,
                  sets: ex.sets.map((candidate) =>
                    candidate.id !== setId
                      ? candidate
                      : {
                          ...candidate,
                          isCompleted,
                          completedAt: isCompleted
                            ? new Date(
                                Math.max(
                                  Date.parse(prev.startedAt ?? "") || 0,
                                  Math.min(
                                    currentActivityCommandTime() ?? Date.now(),
                                    Date.now(),
                                  ),
                                ),
                              ).toISOString()
                            : null,
                        },
                  ),
                },
          ),
        };
      });
    },
    [updateSession, options.timerAutoStart, options.defaultRestSeconds],
  );
  const handleCompleteSet = useCallback(
    (index: number, setId: string) => {
      const exercise = currentSession.exercises[index];
      if (exercise) setCompletion(setId, exercise.id);
    },
    [currentSession.exercises, setCompletion],
  );
  const completeExercise = useCallback(
    (index: number) => {
      updateSetsForExercise(index, (sets) =>
        sets.map((set) => ({
          ...set,
          isCompleted: true,
          completedAt: set.completedAt ?? new Date().toISOString(),
        })),
      );
    },
    [updateSetsForExercise],
  );
  const handleDeleteSet = useCallback(
    (index: number, setId: string) => {
      updateSetsForExercise(index, (sets) =>
        sets
          .filter((set) => set.id !== setId)
          .map((set, i) => ({ ...set, setNumber: i + 1 })),
      );
    },
    [updateSetsForExercise],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Complete Workout
  // ─────────────────────────────────────────────────────────────────────────
  const completeWorkoutMutation = useMutation({
    mutationKey: ["wt", "completeWorkout", accountId],
    networkMode: "always",
    mutationFn: finalizeWorkoutSnapshot,
    onSuccess: (_data, snapshot) => {
      if (accountRef.current !== snapshot.accountId) return;
      if (
        acknowledgeWorkoutJournal(
          window.localStorage,
          snapshot.accountId,
          snapshot.workoutId,
          snapshot.endedAt,
        )
      )
        queryClient.setQueryData(
          wtKeys.activeWorkout(snapshot.accountId),
          null,
        );
      queryClient.invalidateQueries({ queryKey: wtKeys.workouts() });
      queryClient.invalidateQueries({ queryKey: wtKeys.dashboardStats() });
    },
  });

  const finishPromises = useRef(new Map<string, Promise<string | null>>());
  const finishWorkout = useCallback((): Promise<string | null> => {
    if (!accountId || accountRef.current !== accountId)
      return Promise.reject(new Error("Workout account changed"));
    const latest = accountId
      ? readWorkoutJournal(window.localStorage, accountId)
      : null;
    if (!latest?.workoutId || !latest.startedAt || !accountId)
      return Promise.resolve(null);
    if (latest.accountId && latest.accountId !== accountId)
      return Promise.reject(new Error("Workout account changed"));
    const finishKey = `${accountId}:${latest.workoutId}`;
    const pending = finishPromises.current.get(finishKey);
    if (pending) return pending;
    const frozen = {
      ...freezeWorkoutSession(
        latest,
        Math.min(currentActivityCommandTime() ?? Date.now(), Date.now()),
        latest.disposition,
      ),
      accountId,
    };
    const commandId = currentActivityCommandId();
    if (
      !latest.endedAt &&
      commandId &&
      !frozen.appliedCommandIds?.includes(commandId)
    )
      frozen.appliedCommandIds = [
        ...(frozen.appliedCommandIds ?? []),
        commandId,
      ];
    writeWorkoutJournal(window.localStorage, accountId, frozen);
    queryClient.setQueryData(wtKeys.activeWorkout(accountId), frozen);
    const task = (async () => {
      await endWorkoutActivity(
        frozen.disposition !== "discarded",
        Date.parse(frozen.endedAt),
        {
          accountId,
          workoutId: frozen.workoutId,
          startedAt: Date.parse(frozen.startedAt),
          workoutName: frozen.workoutName,
        },
      );
      const result = await completeWorkoutMutation.mutateAsync({
        schemaVersion: frozen.weightUnit ? 2 : 1,
        weightUnit: frozen.weightUnit,
        accountId,
        workoutId: frozen.workoutId,
        workoutName: frozen.workoutName,
        startedAt: frozen.startedAt,
        endedAt: frozen.endedAt,
        disposition: frozen.disposition,
        exercises: frozen.exercises,
      });
      return result.id;
    })().finally(() => {
      finishPromises.current.delete(finishKey);
    });
    finishPromises.current.set(finishKey, task);
    return task;
  }, [accountId, queryClient, endWorkoutActivity, completeWorkoutMutation]);

  const finishWorkoutRef = useRef(finishWorkout);
  finishWorkoutRef.current = finishWorkout;
  useEffect(() => {
    if (!currentSession.endedAt || !accountId) return;
    const retry = () => {
      if (navigator.onLine)
        void finishWorkoutRef.current().catch(() => {
          /* Retain terminal journal for the next retry. */
        });
    };
    window.addEventListener("online", retry);
    const interval = window.setInterval(retry, 30000);
    retry();
    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(interval);
    };
  }, [currentSession.endedAt, accountId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Abandon Workout (delete and clear session)
  // ─────────────────────────────────────────────────────────────────────────
  const abandonWorkout = useCallback(async () => {
    if (!accountId || accountRef.current !== accountId)
      throw new Error("Workout account changed");
    const current = readWorkoutJournal(window.localStorage, accountId);
    if (!current?.workoutId || !accountId) return;
    if (current.endedAt)
      throw new Error(
        "This workout is awaiting synchronization. Its final result cannot change.",
      );
    const discarded = freezeWorkoutSession(current, Date.now(), "discarded");
    writeWorkoutJournal(window.localStorage, accountId, discarded);
    queryClient.setQueryData(wtKeys.activeWorkout(accountId), discarded);
    await finishWorkout();
  }, [queryClient, accountId, finishWorkout]);

  // ─────────────────────────────────────────────────────────────────────────
  // Update Workout Name
  // ─────────────────────────────────────────────────────────────────────────
  const setWorkoutName = useCallback(
    (name: string) => {
      updateSession((prev) => ({ ...prev, workoutName: name }));
    },
    [updateSession],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Initialize Session (for starting a new workout or resuming)
  // ─────────────────────────────────────────────────────────────────────────
  const initializeSession = useCallback(
    (name: string, _routineId?: string) => {
      // Only initialize if we don't have an active workout
      if (currentSession.workoutId) return;

      updateSession(() => ({
        workoutId: null,
        weightUnit: "kg",
        workoutName: name,
        exercises: [],
        startedAt: null,
        restTimer: DEFAULT_REST_TIMER,
      }));
    },
    [currentSession.workoutId, updateSession],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Load Routine Exercises (for starting from a routine)
  // ─────────────────────────────────────────────────────────────────────────
  const loadRoutineExercises = useCallback(
    async (
      routineExercises: Array<{
        exerciseId: string | null;
        customExerciseId: string | null;
        exerciseName: string;
        targetSets?: number | null;
        targetReps?: number | null;
        targetWeight?: number | null;
        restSeconds?: number | null;
        primaryMuscle?: string | null;
        equipment?: string | null;
        imageUrl?: string | null;
        gifUrl?: string | null;
        supersetGroup?: string | null;
      }>,
      routineWeightUnit: WeightUnit | null = null,
    ) => {
      if (
        !routineWeightUnit &&
        routineExercises.some((re) => re.targetWeight != null)
      )
        throw new Error("Confirm the routine weight unit before starting");
      await ensureWorkoutStarted();
      const exercises: ExerciseData[] = routineExercises.map((re) => ({
        id: createRandomId(),
        exerciseId: re.exerciseId,
        customExerciseId: re.customExerciseId,
        name: re.exerciseName,
        primaryMuscle: re.primaryMuscle,
        equipment: re.equipment,
        imageUrl: re.imageUrl,
        gifUrl: re.gifUrl,
        supersetGroup: re.supersetGroup,
        sets: Array.from({ length: re.targetSets ?? 3 }, (_, i) => ({
          id: createRandomId(),
          setNumber: i + 1,
          weight: knownWeightKg(re.targetWeight, routineWeightUnit),
          reps: re.targetReps ?? null,
          duration: null,
          restSeconds: re.restSeconds ?? null,
          isCompleted: false,
        })),
      }));
      updateSession((prev) => ({
        ...prev,
        exercises: [...prev.exercises, ...exercises],
      }));
      for (const ex of exercises)
        fetchPreviousSets(ex.id, ex.exerciseId, ex.customExerciseId);
    },
    [ensureWorkoutStarted, updateSession, fetchPreviousSets],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Computed Values
  // ─────────────────────────────────────────────────────────────────────────
  const totalSets = currentSession.exercises.reduce(
    (sum, e) => sum + e.sets.length,
    0,
  );
  const completedSets = currentSession.exercises.reduce(
    (sum, e) => sum + e.sets.filter((s) => s.isCompleted).length,
    0,
  );

  return {
    // State
    hasAppliedCommand: (id: string) =>
      queryClient
        .getQueryData<WorkoutSessionState>(wtKeys.activeWorkout(accountId))
        ?.appliedCommandIds?.includes(id) ?? false,
    session: currentSession,
    workoutId: currentSession.workoutId,
    workoutName: currentSession.workoutName,
    exercises: currentSession.exercises.map((ex) => ({
      ...ex,
      sets: ex.sets.map((set) => ({
        ...set,
        weight: displayWeight(
          set.weight,
          currentSession.weightUnit,
          options.weightUnit ?? "kg",
        ),
      })),
      previousSets: ex.previousSets?.map((set) => ({
        ...set,
        weight: set.weightUnit
          ? displayWeight(
              set.weight,
              set.weightUnit,
              options.weightUnit ?? "kg",
            )
          : null,
      })),
    })),
    recoveryError,
    needsWeightUnit:
      Boolean(currentSession.workoutId) && currentSession.weightUnit == null,
    confirmWeightUnit: (unit: WeightUnit) =>
      updateSession((prev) => {
        if (prev.weightUnit) return prev;
        return {
          ...prev,
          weightUnit: "kg",
          exercises: prev.exercises.map((ex) => ({
            ...ex,
            sets: ex.sets.map((set) => ({
              ...set,
              weight: knownWeightKg(set.weight, unit),
            })),
          })),
        };
      }),
    isPendingCompletion: Boolean(currentSession.endedAt),
    elapsedSeconds,
    totalSets,
    completedSets,
    hasActiveWorkout,
    isStarting: !accountId || isHydratingSession,
    isFinishing: completeWorkoutMutation.isPending,
    notificationPermissionGuidance: permissionGuidance,
    clearNotificationPermissionGuidance: clearPermissionGuidance,
    retryWorkoutNotification,

    // Rest timer state
    restTimerState: currentSession.restTimer,

    // Actions
    initializeSession,
    ensureWorkoutStarted,
    setWorkoutName,
    setExerciseMode: (index: number, mode: "reps" | "time" | "distance") =>
      updateSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((ex, i) =>
          i === index ? { ...ex, trackMode: mode } : ex,
        ),
      })),
    addExercise,
    insertExerciseAt,
    replaceExercise,
    reorderExercisesByIds,
    setExerciseSuperset,
    linkExercisesAsSuperset,
    removeExercise,
    addSet: handleAddSet,
    updateSet: handleUpdateSet,
    completeSet: handleCompleteSet,
    completeSetById: (setId: string) => setCompletion(setId, undefined, true),
    completeExercise,
    deleteSet: handleDeleteSet,
    finishWorkout,
    abandonWorkout,
    loadRoutineExercises,

    // Rest timer actions
    startRestTimer,
    adjustRestTimer,
    updateRestTimer,
    cancelRestTimer,
    completeRestTimer,
  };
}
